// THEMIS screen help (the "?" on the THEMIS list and on a protocol). Plain words; keep it
// in step with the screen in tools/ThemisTool.tsx.
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const themisHelp: ScreenHelpContent = {
  title: 'THEMIS',
  intro:
    'THEMIS holds your protocols. A protocol is a routine made of steps, each with its own timer. You build it once, then press Play and it walks you through the steps one at a time.',
  sections: [
    {
      heading: 'The protocol list',
      body: [
        'Tap a protocol to open it. Tap + Add, then choose Add Protocol or Add Group. A group is a folder to keep protocols together: tap its name to open or close it.',
        'Use ▲ and ▼ to change the order. Tap ⋯ on a protocol to Edit it (rename it or change its group), Archive it, or Delete it. Tap ⋯ on a group to Rename it or Delete Group. Deleting a group keeps its protocols; they just move out of the group.',
        'Archive and Delete both remove the protocol\'s steps, run history and schedule. The protocol itself goes to Trash or Archive.',
      ],
    },
    {
      heading: 'Inside a protocol',
      body: [
        'Each row is one step. Tap the round dot to tick it done. Edit the name or the minutes any time: it saves when you tap out of the box. If you leave minutes empty, a step gets 1 minute.',
        'Add a step at the bottom: type a name, the minutes, and press Add. Use ▲ and ▼ to reorder. Notes opens a note for just that step. Del removes it, after you confirm.',
      ],
    },
    {
      heading: 'Running it',
      items: [
        { term: 'Play', text: 'Starts at the first step that is not ticked, with its timer. If every step is ticked it says Restart and starts again from the top.' },
        { term: 'Pause / Resume', text: 'Holds the timer, then carries on.' },
        { term: 'All Complete', text: 'Ticks every step and finishes the run.' },
        { term: 'Next', text: 'Moves on to the next step that is not ticked. If the timer had run out, the step you finish is ticked.' },
        { term: 'Repeat', text: 'Runs the current step\'s timer again.' },
        { term: 'Abandon', text: 'Ends the run and logs it as abandoned. Leaving with Back during a run does the same, after you confirm.' },
      ],
    },
    {
      heading: 'When a timer ends',
      body: [
        'A "Step Complete!" box appears with a sound and a buzz. Tap Next to move on or Repeat to go again.',
        'If Transition is on (see the ⋯ menu, Settings), a countdown starts instead. Do nothing and the step ticks itself and the next step begins.',
      ],
    },
    {
      heading: 'The ⋯ menu on a protocol',
      items: [
        { term: 'Notes', text: 'A note for the whole protocol. Press Save Notes to keep it.' },
        { term: 'Log', text: 'Each time you ran it: when it started and ended, steps done, time taken, pauses, and whether it finished (success) or was abandoned.' },
        { term: 'Schedule', text: 'Press + Add Schedule, set a time, and tap the days it applies to. Changes save as you make them.' },
        { term: 'Settings', text: 'Transition on or off, and how many seconds the countdown lasts.' },
        { term: 'Reset All Steps', text: 'Unticks every step so you can start fresh.' },
      ],
    },
    {
      heading: 'Good to know',
      body: [
        'Ticks stay on a protocol until you reset them or restart.',
        'A protocol made from a TKNE step shows a "From TKNE" line at the top. Tap it to jump to that step.',
        'You can also run a protocol from a task in METIS, using the THEMIS tab on the task.',
      ],
    },
  ],
};
